import './FileUploadTool.less';
import React from 'react';
import __ from "../../i18n";
import {formatBytesLocalized} from '../../../bootstrap/utils/number';
import DropBox from './components/drop-box/DropBox';
import BrowseButton from './components/browse-button/BrowseButton';
import Gallery, {
    GALLERY_FULL,
    GALLERY_GRID
} from './components/gallery/Gallery';
import Request from '../../../bootstrap/api/Request';
import ResponseError from '../../../bootstrap/api/ResponseError';
import BottomBar, {
    STATUS_EMPTY,
    STATUS_ALL_SUCCESS,
    STATUS_ALL_ERROR,
    STATUS_BOTH_SUCCESS_ERROR,
    STATUS_PROGRESSING
} from './components/bottom-bar/BottomBar';
import FilePlaceholder from "../../widgets/file-placeholder/FilePlaceholder";

export default class FileUploadTool extends React.Component {
    static defaultProps = {
        FileUploadParcel: undefined,
        renderThumbnail: (record) => null,
        renderPlaceholder: (filename, mimeType) => <FilePlaceholder title={filename} mimeType={mimeType}/>,
        maxFiles: 12,
        maxFileSize: 2 * 1024 * 1024,
        allowedExtensions: [],
        allowedMimeTypes: [],
        onDone: (records) => undefined,
        onCancel: () => undefined,
    };

    constructor(props) {
        super(props);

        this.state = {
            fileUploadInfoList: [],
            uploadedRecords: [],
            isReadingFiles: false,
            galleryLayout: this.getGalleryLayout(0)
        };
    }

    getGalleryLayout = (numOfFiles) => {
        if (numOfFiles > 1) {
            return GALLERY_GRID;
        }

        return GALLERY_FULL;
    };

    readFileSequentially = (index, files, numRead, onDone) => {
        const file = files[index];

        const next = () => {
            if (index < numRead - 1) {
                this.readFileSequentially(index + 1, files, numRead, onDone);
                return;
            }
            onDone();
        };

        const error = this.getFileError(file);
        if (error !== null) {
            this.onFileError(error, file);
            next();
            return;
        }

        this.onFileData(file);
        next();
    };

    handleFiles = files => {
        const numRead = Math.min(files.length, this.props.maxFiles);

        this.setState(
            {
                isReadingFiles: true,
                galleryLayout: this.getGalleryLayout(numRead)
            },
            () => this.readFileSequentially(
                0,
                files,
                numRead,
                () => this.setState({isReadingFiles: false})
            )
        );
    };

    /**
     *
     * @param file
     * @param error
     * @param uploading
     * @return {IFileUploadInfo}
     */
    createFileUploadInfo = (file, error, uploading) => {
        return {
            filename: file.name,
            mimeType: file.type,
            record: null,
            error,
            uploading
        };
    };

    onFileData = (file) => {
        const fileUploadInfo = this.createFileUploadInfo(file, null, true);

        this.addFileUploadInfo(fileUploadInfo);
        this.uploadFile(file, fileUploadInfo);
    };

    onFileError = (error, file) => {
        const fileUploadInfo = this.createFileUploadInfo(file, error, false);

        this.addFileUploadInfo(fileUploadInfo);
    };

    addFileUploadInfo = (fileUploadInfo) => {
        this.state.fileUploadInfoList.push(fileUploadInfo);
        this.forceUpdate();
    };

    uploadFile = (file, fileUploadInfo) => {
        const {FileUploadParcel} = this.props;
        const {uploadedRecords} = this.state;

        const onDone = record => {
            uploadedRecords.push(record);
            fileUploadInfo.record = record;
        };

        /**
         * @param {ResponseError} responseError
         */
        const onError = responseError => {
            fileUploadInfo.error = responseError.getMessage();
        };

        const onFinish = () => {
            fileUploadInfo.uploading = false;
            this.forceUpdate();
        };

        Request.add(new FileUploadParcel(file))
            .then(onDone)
            .catch(onError)
            .finally(onFinish);
    };

    getIsUploading = () => {
        return this.state.fileUploadInfoList.some(fileUploadInfo => fileUploadInfo.uploading);
    };

    getStatus = () => {

        if (this.state.isReadingFiles || this.getIsUploading()) {
            return STATUS_PROGRESSING;
        }

        const {uploadedRecords, fileUploadInfoList} = this.state;

        if (fileUploadInfoList.length === 0) {
            return STATUS_EMPTY;
        }

        if (fileUploadInfoList.length === uploadedRecords.length) {
            return STATUS_ALL_SUCCESS;
        }

        if (fileUploadInfoList.length > 0 && uploadedRecords.length === 0) {
            return STATUS_ALL_ERROR;
        }

        return STATUS_BOTH_SUCCESS_ERROR;
    };

    apply = () => {
        this.props.onDone(this.state.uploadedRecords);
    };

    reset = () => {
        this.state.fileUploadInfoList.length = 0;
        this.state.uploadedRecords.length = 0;
        this.forceUpdate();
    };

    filter = () => {
        this.state.fileUploadInfoList = this.state.fileUploadInfoList.filter(fileUploadInfo => fileUploadInfo.error === null);
        this.forceUpdate();
    };

    cancel = () => {
        if (this.props.onCancel) {
            this.props.onCancel();
        }
    };

    /**
     *
     * @param {File} file
     */
    getFileError = (file) => {
        const {allowedMimeTypes, allowedExtensions, maxFileSize} = this.props;

        if (file.size > maxFileSize) {
            return __('File too large: ::size', {size: formatBytesLocalized(file.size)});
        }

        if (allowedMimeTypes.length > 0 && !allowedMimeTypes.includes(file.type)) {
            return __('Invalid type: ::type', {type: file.type});
        }

        const extension = file.name.split('.').pop();

        if (allowedExtensions.length > 0 && !allowedExtensions.map(t => t.toUpperCase()).includes(extension.toUpperCase())) {
            return __('Invalid extension: ::ext', {ext: extension});
        }

        return null;
    }

    render() {
        const {maxFiles, maxFileSize, allowedMimeTypes, allowedExtensions, renderThumbnail, renderPlaceholder} = this.props;
        const {fileUploadInfoList, galleryLayout} = this.state;

        return <div className="file-upload-tool">
                <div className="workspace">
                    {
                        fileUploadInfoList.length > 0
                            ?
                            <Gallery
                                fileUploadInfoList={fileUploadInfoList}
                                layout={galleryLayout}
                                renderThumbnail={renderThumbnail}
                                renderPlaceholder={renderPlaceholder}
                            />
                            :
                            <DropBox onChange={files => this.handleFiles(files)}>
                                <div className="guideline">{__('Drop your file here')}</div>
                                <div className="guideline">{__('or')}</div>
                                <div className="browse">
                                    <BrowseButton
                                        onChange={files => this.handleFiles(files)}
                                        allowedMimeTypes={allowedMimeTypes}
                                    />
                                </div>
                            </DropBox>
                    }
            </div>
            <div className="hint">
                {/*
                    allowedMimeTypes.length > 0 &&
                    <p>{__('Formats: ::types', {types: allowedMimeTypes})}</p>
                */}
                {
                    allowedExtensions.length > 0 &&
                    <p>{__('Formats: ::exts', {exts: allowedExtensions.map(t => t.toUpperCase())})}</p>
                }
                <p>{__('File size: ::size', {size: formatBytesLocalized(maxFileSize)})}</p>
                <p>{__('Limit: ::max(PLURAL, 1 1 file, ! :@ files)', {max: maxFiles})}</p>
            </div>
            <BottomBar
                apply={this.apply}
                reset={this.reset}
                filter={this.filter}
                cancel={this.cancel}
                status={this.getStatus()}
            />
        </div>;
    }
}


